'use client';

import {
  HTMLAttributes,
  forwardRef,
  ForwardedRef,
  useEffect,
  useState,
} from 'react';
import classNames from 'classnames';
import './css/SpinningLoader.scss';

interface SpinningLoaderProps extends HTMLAttributes<HTMLDivElement> {
  state?: string;
  size?: string;
  status?: string;
  additionalContext?: string;
}

export const SpinningLoader = forwardRef(
  (props: SpinningLoaderProps, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      className,
      state = 'active',
      size = 'small',
      status,
      additionalContext,
      ...spreadProps
    } = props;
    const showAdditionalContext = size === 'large' && !!additionalContext;

    const [isAriaLiveRendered, setIsAriaLiveRendered] = useState(false);

    useEffect(() => {
      // Set the state to true after the component mounts
      const timer = setTimeout(() => {
        setIsAriaLiveRendered(true);
      }, 100);

      return () => clearTimeout(timer);
    }, []);

    const classes = classNames({
      'ps-spinning-loader': true,
      [`${className}`]: className,
    });

    /**
     * The content of SpinningLoader will be rendered twice:
     *** One render is visually displayed but hidden from screen reader users.
     *** The other render is not visually displayed but is accessible to screen reader users.
     * This approach addresses an SSR issue where live region content needs to change client-side to be announced,
     * while visual content needs to be rendered server-side to work correctly on load.
     */
    const content = (
      <>
        <div
          className="ps-spinning-loader-icon-wrapper"
          aria-hidden={state === 'active' ? 'true' : 'false'}
        >
          <span className="ps-spinning-loader-icon-text-visually-hidden">
            {state === 'complete' && 'Success: '}
            {state === 'error' && 'Error: '}
          </span>
          <div aria-hidden="true">
            <div className="ps-spinning-loader-circle"></div>
            {state === 'complete' && (
              <div className="ps-spinning-loader-checkmark-wrapper">
                <div className="ps-spinning-loader-checkmark"></div>
              </div>
            )}
            {state === 'error' && (
              <div className="ps-spinning-loader-exclamation"></div>
            )}
          </div>
        </div>

        {status && <p className="ps-spinning-loader-status">{status}</p>}

        {showAdditionalContext && (
          <p className="ps-spinning-loader-additional-context">
            {additionalContext}
          </p>
        )}
      </>
    );

    return (
      <div
        {...spreadProps}
        className={classes}
        data-size={size}
        data-state={state}
        ref={ref}
      >
        <div
          className="ps-spinning-loader-container"
          role="status"
        >
          <div
            aria-hidden="true"
            className="ps-spinning-loader-content-visible"
          >
            {content}
          </div>
          {isAriaLiveRendered && (
            <div className="ps-spinning-loader-content-not-visible">
              {content}
            </div>
          )}
        </div>
      </div>
    );
  }
);
